import { usePaidLeave } from "@/hooks/usePaidLeave";
import { usePayrollRun } from "@/hooks/usePayrollRun";
import type { PayrollEmployee } from "@/api/types";
import { formatMoney } from "@/lib/format";
import "./PayrollCard.css";

/**
 * The next payroll run, broken down.
 *
 * Strategically the most important card on this dashboard, and the reason it
 * exists: the MN Paid Leave line is the ACTUAL compliance work — a shipped,
 * tested calculation engine with 37 passing tests verified against the current
 * software's own output. Everything else on this screen is a demo; that line is
 * real, and it is what connects the booth to the engagement Christy is paying
 * for.
 *
 * FIGURES ARE LIVE as of 2026-09-05. They were static literals — 22,640 gross and
 * 18,415 net — and they had drifted into contradicting the app around them: the
 * Payroll screen computed 18,415 GROSS and 15,750.42 net from the same roster, so
 * this card's "Net pay" was that screen's "Gross wages" one click away. The
 * Dashboard's own payroll KPI already showed the live gross, which put both numbers
 * on ONE screen. Anyone adding along at the booth notices.
 *
 * Totals come from POST /api/payroll/run via usePayrollRun — the same engine and the
 * same call the Payroll screen makes, so the two cannot disagree again. Money is
 * formatted for display only and never summed client-side; PayrollRunTotals is what
 * the server returned.
 *
 * ⚠️ THE ROSTER IS PASSED IN, NOT FETCHED HERE. Dashboard already calls
 * usePayrollRoster for its KPI, so this adds one request (the run) rather than two.
 * Dashboard.tsx's own comment records a measured reason for keeping the run off its
 * mount path: "the extra round trip pushed the live-KPI flash chip past its highlight
 * window under parallel test load". That trade-off was reopened deliberately on
 * 2026-09-05 — two contradicting money figures on the booth's main screen is the worse
 * fault — but it means `web/e2e/dashboard.spec.ts` is the test to watch, and it should
 * be run before the show.
 */
interface PayrollCardProps {
  /** Present only when there's a Payroll screen to send "Review run →" to. */
  onReviewRun?: () => void;
  /**
   * The roster Dashboard already fetched. `null` while it loads — the run waits for
   * it rather than firing against an empty list.
   */
  employees?: PayrollEmployee[] | null;
}

export function PayrollCard({ onReviewRun, employees = null }: PayrollCardProps = {}) {
  const { totals } = usePayrollRun(employees);

  // The MN Paid Leave line is live too, and it is the line that most needed to be.
  // The card's own note calls it "the ACTUAL compliance work ... what connects the booth
  // to the engagement Christy is paying for" — a stale figure there is the worst one on
  // the card, and leaving it hardcoded beside three live ones would rebuild the exact
  // drift this change removes. employeePremium is the employee-side deduction, which is
  // what this row has always shown.
  const paidLeave = usePaidLeave(employees);

  // An em dash, matching how the Dashboard's KPI cards render an unresolved figure.
  // Never a zero and never a stale literal: "not computed yet" and "computed as nothing"
  // must not look the same on a screen about money.
  const money = (value: number | undefined) =>
    totals === undefined || value === undefined ? "—" : formatMoney(value);

  const headcount = employees?.length ?? null;

  return (
    <section className="payroll" aria-labelledby="payroll-heading">
      <div className="payroll__head">
        <h2 id="payroll-heading" className="payroll__title">
          Next payroll
        </h2>
        <span className="payroll__when">
          Fri{headcount === null ? "" : ` · ${headcount} employee${headcount === 1 ? "" : "s"}`}
        </span>
      </div>

      <dl className="payroll__lines">
        <div className="payroll__line payroll__amount-line">
          <dt>Gross</dt>
          <dd>{money(totals?.gross)}</dd>
        </div>
        <div className="payroll__line payroll__amount-line">
          {/*
            THE LIST HAS TO ADD UP, AND IT DID NOT.

            MEASURED ON THE DEMO LAPTOP, 2026-09-07. The card read Gross $18,415.00,
            Withholding −$1,680.00, MN Paid Leave −$81.04, Net pay $15,750.42 — and no
            subset of those deductions produces that net. Subtract both and you get
            $16,653.96; subtract only withholding and you get $16,735.00. Neither is the
            number in bold at the bottom.

            The cause was FICA. It is the largest deduction on this run at $984.58 and it
            was the one line the card did not show, so a four-line paycheck summary invited
            arithmetic it could never satisfy. This is the screen a prospect reads across a
            booth table while somebody else is talking, and adding up a paycheck is exactly
            what a farmer does to it. With this line present the list closes exactly:
            18,415.00 − 984.58 − 1,680.00 = 15,750.42.

            Order matches the Payroll screen's run summary — gross, FICA, withholding, net —
            so the two never read as different arithmetic about the same run.
          */}
          <dt>FICA / Medicare</dt>
          <dd className="payroll__deduct">−{money(totals?.ficaMedicare)}</dd>
        </div>
        <div className="payroll__line payroll__amount-line">
          <dt>Withholding</dt>
          <dd className="payroll__deduct">−{money(totals?.withholding)}</dd>
        </div>
        <div className="payroll__line payroll__amount-line">
          {/* A deduction line, and nothing more.
          
              It carried a "new" badge and a full-width button of its own beside "Review
              run". Between that, a card on the Payroll screen and a separate destination,
              MN Paid Leave was advertised in three places on a card whose other two
              deductions are announced in none. It is a payroll tax: it belongs in the
              withholding list at the same weight as the others.

              WHAT THE ASIDE IS FOR. Giving it equal weight is right, and it also put a
              deduction into a list whose total does not yet apply it: the payroll engine's
              net pay is a FEDERAL figure, and MN Paid Leave is computed by a separate
              engine. The Payroll screen already says so in its own words — "the net pay
              above does not include the MN Paid Leave withholding ... a correct federal
              paycheck, not the final 2026 one" — and this card, which shows
              the same run on the screen a prospect actually reads, said nothing. Six words
              rather than that whole sentence, because the card is small and the claim is
              narrow: this line is real, and the bold number below has not applied it. */}
          <dt>MN Paid Leave<span className="payroll__aside"> — not in the net below yet</span></dt>
          <dd className="payroll__deduct">
            {paidLeave.data === null ? "—" : `−${formatMoney(paidLeave.data.totals.employeePremium)}`}
          </dd>
        </div>
        <div className="payroll__line payroll__line--total payroll__amount-line">
          <dt>Net pay</dt>
          <dd>{money(totals?.netPay)}</dd>
        </div>
      </dl>

      {/* Both destinations are full-width buttons rather than a link tucked inside
          the MN Paid Leave <dt> above. That was the first shape tried and it failed
          on the phone: a text link inside a flex <dt> is a small target sitting in a
          row the fixed bottom nav overlaps, so it was neither comfortably tappable
          nor reliably reachable. A real button row is honest about being an action
          and meets the tap-target minimum at every width. */}
      {onReviewRun && (
        <div className="payroll__actions">
          {onReviewRun && (
            <button type="button" className="btn btn--ghost payroll__action-btn" onClick={onReviewRun}>
              Review run<span aria-hidden="true"> →</span>
            </button>
          )}
          
        </div>
      )}
    </section>
  );
}
